import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination — compact numbered pager. Renders nothing if totalPages <= 1.
 * Shows up to 5 page-number buttons centered on the active page.
 */
export default function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  // Build a windowed list of pages around the active one (max 5)
  const window = 2;
  let start = Math.max(1, page - window);
  let end = Math.min(totalPages, page + window);
  if (end - start < 4) {
    if (start === 1) end = Math.min(totalPages, start + 4);
    else if (end === totalPages) start = Math.max(1, end - 4);
  }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  const goto = (p) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    if (clamped !== page) onChange(clamped);
  };

  const btnBase =
    "h-8 min-w-[32px] px-2 rounded-sm text-xs font-mono font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed";

  return (
    <div
      data-testid="leads-pagination"
      className="flex items-center justify-center gap-1.5 py-6"
    >
      <button
        data-testid="pagination-prev"
        onClick={() => goto(page - 1)}
        disabled={page <= 1}
        className={`${btnBase} text-ink-700 border border-ink-200 hover:bg-ink-100`}
      >
        <ChevronLeft className="w-3.5 h-3.5 inline" />
      </button>

      {start > 1 && (
        <>
          <button
            onClick={() => goto(1)}
            className={`${btnBase} text-ink-700 border border-ink-200 hover:bg-ink-100`}
          >
            1
          </button>
          {start > 2 && <span className="text-ink-400 text-xs px-1">…</span>}
        </>
      )}

      {pages.map((p) => (
        <button
          key={p}
          data-testid={`pagination-page-${p}`}
          onClick={() => goto(p)}
          className={`${btnBase} ${
            p === page
              ? "bg-ink-900 text-white border border-ink-900"
              : "text-ink-700 border border-ink-200 hover:bg-ink-100"
          }`}
        >
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && (
            <span className="text-ink-400 text-xs px-1">…</span>
          )}
          <button
            onClick={() => goto(totalPages)}
            className={`${btnBase} text-ink-700 border border-ink-200 hover:bg-ink-100`}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        data-testid="pagination-next"
        onClick={() => goto(page + 1)}
        disabled={page >= totalPages}
        className={`${btnBase} text-ink-700 border border-ink-200 hover:bg-ink-100`}
      >
        <ChevronRight className="w-3.5 h-3.5 inline" />
      </button>
    </div>
  );
}
